import 'regenerator-runtime/runtime';
import * as Sentry from '@sentry/browser';
import { NOT_FOUND, redirect } from 'redux-first-router';
import { getRestrictedContent } from './actions/environment';
import { logoutAccount, createCheckout } from './actions/checkout';
import { fetchData, fetchCheckout } from './utils/async';
import { getCookie, deleteCookie, deleteAllCookies } from './utils/dom';

// Customer specific data is loaded in frontend.
// Generic data for all clients is SSR
const clientUpdate = (dispatch, getState) => {
	// Generate random information on page load
	dispatch({ type: 'CLEAR_ERRORS' });
	dispatch({ type: 'RANDOM' });

	if (getState().checkout?.account?.accessToken && !getState().environment?.downloads?.intranetDownloads) {
		dispatch(getRestrictedContent('downloads/intranet-downloads', false, {})).then((downloads) => (
			dispatch({ type: 'DOWNLOADS', downloads })
		)).catch(() => {
			console.log('DEBUG: intranet downloads not updated');
		});
	}

	if (typeof window !== 'undefined') {
		if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
		window.dataLayer.push({
			event: 'PageView',
			values: {
				path: getState().location.pathname,
			},
		});
	}
};

const clientInit = async (dispatch, getState) => {
	clientUpdate(dispatch, getState);

	const { setup } = getState().environment;
	const { selection = {}, destorySelection = false, account = {} } = getState().checkout;
	let selectionData = {};
	let token = null;

	// destroy selection based on current status in checkout.
	if (destorySelection || (typeof document !== 'undefined' && getCookie('receiptShown'))) {
		dispatch({ type: 'DESTROY_SELECTION' });
		return dispatch({ type: 'START' });
	}

	if (selection.token) token = selection.token;
	if (!token && typeof document !== 'undefined') token = getCookie('selection');
	const selectionCache = JSON.parse(localStorage.getItem('selectionCache'));

	if (token === '1') {
		deleteCookie('selection');
		token = null;
	}

	if (token) {
		if (selectionCache) dispatch({ type: 'INIT_SELECTION_CACHE', initCache: selectionCache });

		// All other pages except for receipt
		selectionData = fetchCheckout('checkoutRetrieve', setup.market, { id: token });

		try {
			selectionData = await selectionData;
		} catch (err) {
			Sentry.withScope((scope) => {
				scope.setContext('customer', { err, setup });
				Sentry.captureMessage('SELECTION ERROR: An error occurred when trying to retrieve previous selection');
			});
			dispatch({ type: 'CHECKOUT_ERROR' });
		}

		// Is the order completed? Delete the token.
		if (selectionData?.data?.node?.id && !selectionData?.data?.node?.completedAt) {
			dispatch({ type: 'UPDATE_SELECTION', data: selectionData.data.node });
		} else {
			Sentry.withScope((scope) => {
				scope.setContext('customer', { setup, market: setup.market });
				Sentry.captureMessage('SELECTION NOT FOUND: While retrieving previous selection all data was not returned');
			});

			// Recreate selection
			if (selectionCache) {
				dispatch({ type: 'RECREATE_SELECTION' });
				const lineItems = selectionCache.map(({ selectionVariantId, quantity = 1 }) => (
					{ variantId: selectionVariantId, quantity: parseInt(quantity, 10) }
				));

				try {
					selectionData = await dispatch(createCheckout({ lineItems, skipUpdate: true }));
					if (!selectionData) throw new Error('Selection not created');
				} catch (err) {
					Sentry.withScope((scope) => {
						scope.setContext('customer', { setup, lineItems, err });
						Sentry.captureMessage('SELECTION UNABLE TO BE RECREATED: Using selection cache was not able to re-create selection');
					});

					// if unable to find products then
					dispatch({ type: 'DESTROY_SELECTION' });
					deleteCookie('selection');
					token = null;
				}
			} else {
				// if unable to find products then
				dispatch({ type: 'DESTROY_SELECTION' });
				deleteCookie('selection');
				token = null;
			}
		}
	}

	// Retrieve downloads/resources
	try {
		const downloadFetch = fetchData('downloads/technical-downloads/press-downloads');
		let restrictedDownloadFetch = {};
		// Check to see if we're logged in.
		if (account.accessToken) {
			restrictedDownloadFetch = dispatch(getRestrictedContent('downloads/intranet-downloads', false, {}));
		}
		const [downloads, restrictedDownloads] = await Promise.all([downloadFetch, restrictedDownloadFetch]);
		dispatch({ type: 'DOWNLOADS', downloads: { ...downloads, ...restrictedDownloads } });
	} catch (err) {
		console.log('DEBUG: downloads not retrieved');
	}


	// If selection is set to another country than default change country data to that of selection.
	if (selectionData.location && selectionData.location.country !== setup.country) {
		let whichCountry = selectionData.location.country;
		if (!setup.countries[whichCountry]) whichCountry = '0';
		const countryData = setup.countries[whichCountry];

		dispatch({ type: 'UPDATE_COUNTRY', country: countryData.country, market: countryData.market, pricelist: countryData.pricelist });
	}

	// Ensure that we have the product cache for the current market and pricelist.
	// We load this in asyncronously because it's needed for search and will be needed for other pages if not already included.
	await retrieveProductCache(dispatch, getState);

	if (token) {
		const { products: productCache, productIdLookup = {} } = getState().productCache[setup.market][setup.pricelist];
		dispatch({ type: 'REPLACE_SELECTION_CACHE', productCache, productIdLookup });
	}

	let favourites = typeof localStorage !== 'undefined' ? JSON.parse(localStorage.getItem('favourites')) : false;
	if (favourites) {
		const productCache = getState().productCache[setup.market][setup.pricelist];
		favourites = favourites.filter((favourite) => productCache.products[favourite] !== undefined);
		dispatch({ type: 'UPDATE_FAVOURITES', favourites });
	}

	const paymentInfo = typeof localStorage !== 'undefined' ? JSON.parse(localStorage.getItem('paymentInfo')) : false;
	if (paymentInfo) {
		if (selectionData?.selection?.items && selectionData.selection.items.length > 0 && paymentInfo?.additionalInformation && paymentInfo.additionalInformation.length > 0) {
			// Ensure custom product data is synced with basket.
			const additionalInformation = paymentInfo.additionalInformation.filter((item) => selectionData.selection.items.filter((sItem) => sItem.item === item.item).length > 0);
			dispatch({ type: 'INIT_PAYMENT_INFO', paymentInfo: { ...paymentInfo, additionalInformation } });
		} else if ((!token || !selectionData?.selection?.items || selectionData.selection.items.length === 0) && paymentInfo?.additionalInformation && paymentInfo.additionalInformation.length > 0) {
			// Remove all additional information as basket is empty.
			dispatch({ type: 'INIT_PAYMENT_INFO', paymentInfo: { ...paymentInfo, additionalInformation: [] } });
		} else {
			dispatch({ type: 'INIT_PAYMENT_INFO', paymentInfo });
		}
	}

	dispatch({ type: 'SITE_UPDATED' });

	return getState();
};

const retrieveProductCache = async (dispatch, getState) => {
	const { setup, materials = [], pdpSwatches = [], plpSwatches = [] } = getState().environment;
	const { previewCode, country, market, pricelist } = setup;
	let productCache;

	if (getState().productCache[market] && getState().productCache[market][pricelist]) return getState().productCache[market][pricelist];

	try {
		const body = previewCode ? { previewCode } : {};
		productCache = await fetchData(`product-cache/${market}/${pricelist}/`, body);
	} catch (err) {
		console.log('DEBUG: product cache not loaded');
	}

	dispatch({ type: 'PRODUCT_CACHE', market, pricelist, setup, productCache, materials, pdpSwatches, plpSwatches });
	return dispatch({ type: 'UPDATE_COUNTRY', market, pricelist, country });
};


const languagePrefix = ''; // '/:language?';
const useLanguage = (str = '') => `${languagePrefix}${str}`;

export default {
	CLIENT_INIT: {
		thunk: async (dispatch, getState) => clientInit(dispatch, getState).catch((err) => console.log('DEBUG: error initiating client site data', err)),
	},
	RETRIEVE_PRODUCT_CACHE: {
		thunk: async (dispatch, getState) => (
			retrieveProductCache(dispatch, getState)
		),
	},
	STATIC: {
		path: '/static/:noop?',
	},
	REFERRAL: {
		path: '/ref',
		thunk: async (dispatch, getState, bag) => {
			const { location, environment } = getState();
			const { getParameters = {}, setup } = environment;
			const { hash = '' } = bag.action.payload;
			let path = '';
			let search = '';
			const goTo = Object.keys(getParameters).reduce((acc, param) => (param.toLowerCase() === 'goto' ? getParameters[param] : acc), '');
			if (goTo) path = decodeURIComponent(goTo).replace(/^\/+/, '');
			if (path.indexOf('#') !== -1) path = path.slice(0, path.indexOf('#'));
			if (path.indexOf('?') !== -1) {
				search = path.slice(path.indexOf('?'));
				path = path.slice(0, path.indexOf('?'));
			} else if (location.search) {
				search = `?${location.search}`;
			}
			if (path === '/' || path === '') return dispatch(redirect({ type: 'START', payload: { language: setup.language, path: '', search, hash } }));
			return dispatch(redirect({ type: 'CATEGORY_PRODUCT_PAGE', payload: { language: setup.language, path, search, hash } }));
		},
	},
	START: {
		path: useLanguage('/'),
		thunk: async (dispatch, getState, bag) => {
			dispatch({ type: 'PAGE_LOADING', initiated: Date.now() });
			const { environment, location, pageData } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages } = environment.setup;
			const paths = languages.length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: '' }), {}) : path;

			// best guess that it's start page otherwise cached...
			const body = environment.setup.previewCode ? { previewCode: environment.setup.previewCode } : {};
			const startPage = !pageData || !pageData.start ? fetchData('', body) : pageData.start;
			const { hash = '' } = bag.action.payload;
			let startPageData;
			clientUpdate(dispatch, getState);

			const currentHash = typeof window !== 'undefined' && window.location.hash !== '' && window.location.hash !== '#' ? window.location.hash : hash;
			dispatch({ type: 'UPDATE_HASH', currentHash });

			try {
				startPageData = await startPage;
			} catch (err) {
				console.log('DEBUG: start page data not retrieved');
			}

			dispatch({ type: 'PAGE_DATA', data: startPageData, page: 'start' });
			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'start' });
		},
	},
	CLEAR_COOKIES: {
		path: useLanguage('/clear-cookies'),
		thunk: async (dispatch) => {
			deleteAllCookies();
			return dispatch(redirect({ type: 'START' }));
		},
	},
	SEARCH: {
		path: useLanguage('/search'),
		thunk: async (dispatch, getState, bag) => {
			dispatch({ type: 'PAGE_LOADING', initiated: Date.now() });
			const { environment, location } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages } = environment.setup;
			const paths = languages.length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'search' }), {}) : path;
			const { hash = '' } = bag.action.payload;

			clientUpdate(dispatch, getState);

			const currentHash = typeof window !== 'undefined' && window.location.hash !== '' && window.location.hash !== '#' ? window.location.hash : hash;
			dispatch({ type: 'UPDATE_HASH', currentHash });

			if (location.query && Object.keys(location.query).length > 0 && Object.keys(location.query)[0] !== '') {
				if (Object.keys(getState().location.query)[0] === 'q') dispatch({ type: 'SEARCH', text: getState().location.query.q });
				else dispatch({ type: 'SEARCH', text: Object.keys(getState().location.query)[0] });
			}

			await retrieveProductCache(dispatch, getState);
			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'search' });
		},
	},
	SELECTION_PAYMENT_LINK: {
		path: useLanguage('/selection/link/:items+'),
		thunk: async (dispatch, getState) => {
			dispatch({ type: 'PAGE_LOADING', initiated: Date.now() });
			const { environment, location } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages } = environment.setup;
			const { items = '' } = location.payload;
			const paths = languages.length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'selection' }), {}) : path;

			await retrieveProductCache(dispatch, getState);

			// items === variantIdxquantity/...
			const lineItems = items.split('/').map((item) => {
				const [variantId, quantity] = item.split('x');
				return { variantId: `gid://shopify/ProductVariant/${variantId}`, quantity: parseInt(quantity, 10) };
			});

			await dispatch(createCheckout({ lineItems, skipUpdate: true }));

			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'selection' });
		},
	},
	SELECTION: {
		path: useLanguage('/selection'),
		thunk: async (dispatch, getState) => {
			dispatch({ type: 'PAGE_LOADING', initiated: Date.now() });
			const { environment, location, pageData } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages } = environment.setup;
			const paths = languages.length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'selection' }), {}) : path;

			const body = environment.setup.previewCode ? { previewCode: environment.setup.previewCode } : {};
			const selectionPage = !pageData || !pageData.selection ? fetchData('selection', body) : pageData.selection;
			let selectionPageData;
			clientUpdate(dispatch, getState);

			try {
				selectionPageData = await selectionPage;
			} catch (err) {
				console.log('DEBUG: failed page data not retrieved');
			}

			dispatch({ type: 'PAGE_DATA', data: selectionPageData, page: 'selection' });
			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'selection' });
		},
	},
	ACCOUNT: {
		path: useLanguage('/account'),
		thunk: async (dispatch, getState) => {
			dispatch({ type: 'PAGE_LOADING' });
			const { environment, location } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages } = environment.setup;
			const paths = languages.length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'account' }), {}) : path;

			clientUpdate(dispatch, getState);

			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'account' });
		},
	},
	ACCOUNT_ACTIVATE: {
		path: useLanguage('/account/activate/:customerId/:activationToken'),
		thunk: async (dispatch, getState) => {
			dispatch({ type: 'PAGE_LOADING' });
			const { environment, location } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages } = environment.setup;
			const paths = languages.length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'account' }), {}) : path;

			clientUpdate(dispatch, getState);

			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'accountActivate' });
		},
	},
	REMEMBER: {
		path: useLanguage('/account/reset/:customerId/:resetToken'),
		thunk: async (dispatch, getState) => {
			dispatch({ type: 'PAGE_LOADING' });
			const { environment, location } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages } = environment.setup;
			const paths = languages.length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'remember' }), {}) : path;

			clientUpdate(dispatch, getState);

			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'remember' });
		},
	},
	LOG_OUT: {
		path: useLanguage('/logout'),
		thunk: async (dispatch, getState) => {
			dispatch({ type: 'PAGE_LOADING' });
			const { environment, location } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages, language } = environment.setup;
			const paths = languages.length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'logout' }), {}) : path;

			// Remove token
			dispatch(logoutAccount());

			dispatch({ type: 'PAGE_LOADED', paths, pageType: 'logout' });
			return dispatch(redirect({ type: 'START', payload: { language, path: '/' } }));
		},
	},
	CATEGORY_PRODUCT_PAGE: {
		path: useLanguage('/:path+'),
		thunk: async (dispatch, getState, bag) => {
			dispatch({ type: 'PAGE_LOADING', initiated: Date.now() });
			/*
				USE CASES:
				/women/clothing/dresses/harper - Product
				/women/clothing/harper - Product
				/women/clothing - Category
				/en/women/clothing - Category with localization
				/sv/dam/kläder - Category with right localization but wrong locale - redirect to english version.
				/en/page
				/page
			*/
			const { location, environment, pageData = {} } = getState();
			const { setup, restrictedPages = [] } = environment;
			const { hash = '', fakeUpdate = false } = bag.action.payload;
			let language = languagePrefix !== '' ? location.payload.language : false;
			// Clean any encoded URI characters
			let path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			let search = '';

			// Remove any hashes from path
			if (path.indexOf('#') !== -1) path = path.slice(0, path.indexOf('#'));
			// Remove any queries from path
			if (path.indexOf('?') !== -1) {
				search = path.slice(path.indexOf('?'));
				path = path.slice(0, path.indexOf('?'));
			} else if (location.search) {
				search = `?${location.search}`;
			}
			// "language" is probably not a language but a path.
			if (!language && language.length > 2) {
				path = `${language}/${path}`;
				language = null;
			}

			if (fakeUpdate) return dispatch({ type: 'PAGE_LOADED', paths: path, pageType: 'category' });

			let isRestricted = false;
			if (restrictedPages.length > 0) {
				isRestricted = restrictedPages.filter((page) => page[setup.language] === path).length > 0;
			}

			// Get content for site.
			const body = setup.previewCode ? { previewCode: setup.previewCode } : {};
			// Get content from state or if log in required send authentication tokens or just fetch page.
			const page = pageData[path] || (isRestricted ? dispatch(getRestrictedContent(path, false, body)) : fetchData(path, body));
			let pageDataReturn = {};
			clientUpdate(dispatch, getState);

			try {
				[, pageDataReturn] = await Promise.all([retrieveProductCache(dispatch, getState), page]);
			} catch (err) {
				console.log(`DEBUG: failed page data not retrieved. ${path}`, err);
			}


			if (pageDataReturn && pageDataReturn.is404) {
				dispatch({ type: NOT_FOUND });
				return dispatch({ type: 'PAGE_LOADED', paths: path, pageType: '404' });
			}

			if (pageDataReturn && pageDataReturn.paths) {
				if (language !== false && path !== pageDataReturn.paths[language] && !pageDataReturn.is404 && pageDataReturn.paths[language]) {
					return dispatch(redirect({ type: 'CATEGORY_PRODUCT_PAGE', payload: { language, path: pageDataReturn.paths[language], search, hash } }));
				}
			}

			dispatch({ type: 'PAGE_DATA', data: pageDataReturn, page: path });

			const currentHash = typeof window !== 'undefined' && window.location.hash !== '' && window.location.hash !== '#' ? window.location.hash : hash;
			dispatch({ type: 'UPDATE_HASH', hash: currentHash });

			if (['product', 'category'].indexOf(pageDataReturn.pageType) !== -1) {
				const hashFilter = [];
				const filterPath = pageDataReturn.pageType === 'product' ? path.slice(0, path.lastIndexOf('/')) : path;

				if (path.indexOf('outdoor') !== -1) {
					hashFilter.push({ outdoor: true });
				}

				if (currentHash) {
					currentHash.replace(/^[?#]/, '').split('&').forEach((filter) => {
						const filterParts = filter.split('=');
						if (filterParts.length === 1) {
							hashFilter.push({ [filterParts[0]]: true });
						} else {
							hashFilter.push({ [filterParts[0]]: filterParts[1].split(',') });
						}
					});
				}

				dispatch({ type: 'RESET_FILTER' });
				dispatch({ type: 'FILTER', filterArray: [{ categories: filterPath }, ...hashFilter], force: true, skipUrlUpdate: true, setup: environment.setup, currentPath: filterPath });
			}

			// if (listingDataReturn) dispatch({ type: 'LISTING', data: listingDataReturn, listing: listingDataReturn.paths.replace('listing/', '') });
			return dispatch({ type: 'PAGE_LOADED', paths: pageDataReturn.paths || path, pageType: pageDataReturn.pageType || 'page' });
		},
	},
};
